import { call, put, takeEvery, select } from 'redux-saga/effects';
import { tenantsApiService, Tenant, CreateTenantDto, UpdateTenantDto, TenantFilters } from '../api/tenants-api';
import {
  setLoading,
  setError,
  setTenants,
  setPagination,
  setCurrentTenant,
  setStats,
  addTenant,
  removeTenant,
  updateTenantInList,
} from './tenants-slice';

// Action types para la saga
export const TENANTS_ACTIONS = {
  FETCH_TENANTS: 'tenants/fetchTenants',
  FETCH_TENANT: 'tenants/fetchTenant',
  CREATE_TENANT: 'tenants/createTenant',
  UPDATE_TENANT: 'tenants/updateTenant',
  DELETE_TENANT: 'tenants/deleteTenant',
  UPDATE_TENANT_STATUS: 'tenants/updateTenantStatus',
  FETCH_TENANT_STATS: 'tenants/fetchTenantStats',
  CHECK_DELETE_RESTRICTIONS: 'tenants/checkDeleteRestrictions',
} as const;

// Types para las acciones de la saga
interface FetchTenantsAction {
  type: string;
  payload?: TenantFilters;
}

interface FetchTenantAction {
  type: string;
  payload: string; // tenantId
}

interface CreateTenantAction {
  type: string;
  payload: CreateTenantDto;
}

interface UpdateTenantAction {
  type: string;
  payload: { id: string; data: UpdateTenantDto };
}

interface DeleteTenantAction {
  type: string;
  payload: string; // tenantId
}

interface UpdateTenantStatusAction {
  type: string;
  payload: { id: string; status: Tenant['status'] };
}

interface CheckDeleteRestrictionsAction {
  type: string;
  payload: string; // tenantId
}

// Fetch tenants saga
function* fetchTenantsSaga(action: FetchTenantsAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const state = yield select();
    const filters = action.payload || state.tenants.filters;

    const response = yield call(tenantsApiService.getTenants, filters);
    
    yield put(setTenants(response.data || []));
    yield put(setPagination({
      page: response.page || 1,
      limit: response.limit || 10,
      total: response.total || 0,
      totalPages: response.totalPages || 0,
    }));

  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar tenants';
    yield put(setError(errorMessage));
    yield put(setTenants([]));
    yield put(setPagination({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    }));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch single tenant saga
function* fetchTenantSaga(action: FetchTenantAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const tenant: Tenant = yield call(tenantsApiService.getTenant, action.payload);
    yield put(setCurrentTenant(tenant));

  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar tenant';
    yield put(setError(errorMessage));
    yield put(setCurrentTenant(null));
  } finally {
    yield put(setLoading(false));
  }
}

// Create tenant saga
function* createTenantSaga(action: CreateTenantAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const newTenant: Tenant = yield call(tenantsApiService.createTenant, action.payload);
    yield put(addTenant(newTenant));
    
    // Actualizar paginación
    const state = yield select();
    yield put(setPagination({
      ...state.tenants.pagination,
      total: state.tenants.pagination.total + 1,
    }));

    // Refresh stats after creating
    yield call(fetchTenantStatsSaga);

    return { success: true, data: newTenant };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al crear tenant';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Update tenant saga
function* updateTenantSaga(action: UpdateTenantAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedTenant: Tenant = yield call(
      tenantsApiService.updateTenant,
      action.payload.id,
      action.payload.data
    );
    yield put(updateTenantInList(updatedTenant));
    
    // Si es el tenant actual, actualizarlo también
    const state = yield select();
    if (state.tenants.currentTenant?.id === updatedTenant.id) {
      yield put(setCurrentTenant(updatedTenant));
    }

    // Refresh stats after updating
    yield call(fetchTenantStatsSaga);

    return { success: true, data: updatedTenant };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar tenant';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Delete tenant saga
function* deleteTenantSaga(action: DeleteTenantAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    // Obtener el estado antes de eliminar
    const stateBeforeDelete = yield select();
    const tenantToDelete = stateBeforeDelete.tenants.tenants.find((t: Tenant) => t.id === action.payload);
    const tenantName = tenantToDelete?.name || 'Cliente';
    const currentPage = stateBeforeDelete.tenants.pagination.page;
    const currentLimit = stateBeforeDelete.tenants.pagination.limit;
    const tenantsInCurrentPage = stateBeforeDelete.tenants.tenants.length;

    yield call(tenantsApiService.deleteTenant, action.payload);
    yield put(removeTenant(action.payload));
    
    // Obtener el estado después de eliminar
    const stateAfterDelete = yield select();
    const remainingTenants = stateAfterDelete.tenants.tenants.length;
    const newTotal = Math.max(0, stateBeforeDelete.tenants.pagination.total - 1);
    
    // Calcular nueva totalPages
    const newTotalPages = newTotal === 0 ? 0 : Math.max(1, Math.ceil(newTotal / currentLimit));
    
    // Si la página actual quedó vacía (no hay más tenants en la página actual) y no estamos en la página 1
    // y todavía hay registros totales, ir a la página anterior
    if (remainingTenants === 0 && currentPage > 1 && newTotal > 0) {
      const previousPage = Math.max(1, currentPage - 1);
      
      // Actualizar filtros con la página anterior
      const updatedFilters = {
        ...stateBeforeDelete.tenants.filters,
        page: previousPage,
        limit: currentLimit,
      };
      
      // Recargar tenants con la página anterior
      yield call(fetchTenantsSaga, {
        type: TENANTS_ACTIONS.FETCH_TENANTS,
        payload: updatedFilters,
      });
    } else {
      // Si no quedó vacía o estamos en página 1, solo actualizar el total
      yield put(setPagination({
        ...stateAfterDelete.tenants.pagination,
        total: newTotal,
        totalPages: newTotalPages,
      }));
    }

    // Si es el tenant actual, limpiarlo
    if (stateBeforeDelete.tenants.currentTenant?.id === action.payload) {
      yield put(setCurrentTenant(null));
    }

    // Refresh stats after deleting
    yield call(fetchTenantStatsSaga);

    // Notificación de éxito - se manejará desde el componente o un middleware de notificaciones
    // Por ahora, la saga solo actualiza el estado
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al eliminar tenant';
    yield put(setError(errorMessage));
    // Notificación de error - se manejará desde el componente
  } finally {
    yield put(setLoading(false));
  }
}

// Update tenant status saga
function* updateTenantStatusSaga(action: UpdateTenantStatusAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedTenant: Tenant = yield call(
      tenantsApiService.updateTenantStatus,
      action.payload.id,
      action.payload.status
    );
    yield put(updateTenantInList(updatedTenant));
    
    // Si es el tenant actual, actualizarlo también
    const state = yield select();
    if (state.tenants.currentTenant?.id === updatedTenant.id) {
      yield put(setCurrentTenant(updatedTenant));
    }

    // Refresh stats after updating status
    yield call(fetchTenantStatsSaga);

    return { success: true, data: updatedTenant };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cambiar estado del tenant';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch tenant stats saga
function* fetchTenantStatsSaga() {
  try {
    const stats = yield call(tenantsApiService.getTenantStats);
    yield put(setStats(stats));
  } catch (error: any) {
    // No establecer error para stats, solo loguear
  }
}

// Check delete restrictions saga
function* checkDeleteRestrictionsSaga(action: CheckDeleteRestrictionsAction) {
  try {
    const result = yield call(
      tenantsApiService.checkDeleteRestrictions,
      action.payload
    );
    return { success: true, ...result };
  } catch (error: any) {
    // Si el endpoint no existe, devolver restricciones por defecto
    if (error.response?.status === 404) {
      return {
        success: true,
        canDelete: true,
        restrictions: [],
      };
    }
    const errorMessage = error.response?.data?.message || 'Error al verificar restricciones';
    return { success: false, error: errorMessage };
  }
}

// Root tenants saga
export function* tenantsSaga() {
  yield takeEvery(TENANTS_ACTIONS.FETCH_TENANTS, fetchTenantsSaga);
  yield takeEvery(TENANTS_ACTIONS.FETCH_TENANT, fetchTenantSaga);
  yield takeEvery(TENANTS_ACTIONS.CREATE_TENANT, createTenantSaga);
  yield takeEvery(TENANTS_ACTIONS.UPDATE_TENANT, updateTenantSaga);
  yield takeEvery(TENANTS_ACTIONS.DELETE_TENANT, deleteTenantSaga);
  yield takeEvery(TENANTS_ACTIONS.UPDATE_TENANT_STATUS, updateTenantStatusSaga);
  yield takeEvery(TENANTS_ACTIONS.FETCH_TENANT_STATS, fetchTenantStatsSaga);
  yield takeEvery(TENANTS_ACTIONS.CHECK_DELETE_RESTRICTIONS, checkDeleteRestrictionsSaga);
}

export default tenantsSaga;

